import React, { useState, useEffect, useCallback } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { MdDelete, MdAdd, MdCheck, MdExpandMore, MdExpandLess, MdRefresh, MdDownload } from 'react-icons/md';
import { withScreen } from './Screen';
import { toast } from 'react-toastify';
import graphqlClient from "../graphqlClient";
import { gql, useMutation, useQuery, useSuspenseQuery } from '@apollo/client';
import { api } from '../utils/api';
import { OrderRow } from './OrderRow';
import { ImportOrder_SyncFromExternalOrderMutation, ImportOrder_SyncFromExternalOrderMutationVariables, ImportOrderList_GetShopsQuery, ImportOrderList_GetShopsQueryVariables, OrderStatus } from '../../codegen/graphql';
import * as countryCodes from 'country-codes-list';
import { postMessageToLeboncoinExt } from '../lib/leboncoin/LeboncoinExtApi';
import Button from './common/Button';
import { useRequestManager } from './common/RequestManager';
import dayjs from 'dayjs';
import { regionsAndSitesData_byCountryCode, regionsAndSitesData_bySiteId } from '../../common/temu_regionsAndSitesData';
import { Helmet } from 'react-helmet';
import { SALE_CHANNEL } from '../../common/salesChannels';

const CHECK_EXISTING_ORDERS_QUERY = gql`
  query CheckExistingOrders($extIdsAndShopTypes: [IdAndShopTypeInput!]!) {
    orders(filters: { extIdsAndShopTypes: $extIdsAndShopTypes }) {
      nodes {
        orderId
        shopId
        shop {
          shopType
        }
      }
    }
  }
`;

const GET_SHOPS_QUERY = gql`
  query ImportOrderList_GetShops {
    shops {
      id
      shopType
      shopKey
      title
    }
  }
`;

const IMPORTABLE_SHOP_TYPES = new Set(['ebay', 'leboncoin', 'temu', 'aliexpress']);

type Shop = {
  id: string;
  shopType: string;
  shopKey: string;
  title: string;
};

interface ImportOrderListProps {}

function ImportOrderList({}: ImportOrderListProps) {
  const navigate = useNavigate();
  const { trackRequest } = useRequestManager();
  const [orders, setOrders] = useState<any[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedShopIds, setSelectedShopIds] = useState<Set<string>>(new Set());

  const { data: shopsData } = useSuspenseQuery<
    ImportOrderList_GetShopsQuery,
    ImportOrderList_GetShopsQueryVariables
  >(
    GET_SHOPS_QUERY
  );

  const shops: Shop[] = shopsData.shops;
  const importableShops = shops.filter((shop) => IMPORTABLE_SHOP_TYPES.has(shop.shopType));

  
  // Manual import form state
  const [manualImportShopId, setManualImportShopId] = useState<string>('');
  const [manualImportOrderId, setManualImportOrderId] = useState<string>('');
  const [manualImportLang, setManualImportLang] = useState<string>('FR');
  const [manualImportLoading, setManualImportLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!manualImportShopId && importableShops.length > 0) {
      setManualImportShopId(importableShops[0].id);
    }
  }, [importableShops, manualImportShopId]);
  const fetchOrders = useCallback(async () => {
    const selectedShops = importableShops.filter((shop) => selectedShopIds.has(shop.id));

    if (selectedShops.length === 0) {
      setOrders([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const ebayParams = new URLSearchParams([['status', 'NOT_STARTED'], ['status', 'IN_PROGRESS']]);
      const aliexpressParams = new URLSearchParams([
        ['pageSize', '100'],
        ['currentPage', '1'],
        ['createDateStart', dayjs().subtract(3, 'month').format('YYYY-MM-DD HH:mm:ss')],
        ['createDateEnd', dayjs().add(1, 'day').format('YYYY-MM-DD HH:mm:ss')],
        ['orderStatus', 'WAIT_SELLER_SEND_GOODS']
      ]);

      const shopOrderResults = await Promise.all(
        selectedShops.map(async (shop) => {
          switch (shop.shopType) {
            case 'ebay': {
              const params = new URLSearchParams(ebayParams);
              params.set('shop_id', shop.id);
              params.set('lang', manualImportLang);
              const response = await api.get('/ebay/orders?' + params.toString());
              const ebayOrders = await response.json();
              return ebayOrders.orders.map((orderData: any) => ({
                id: '',
                orderId: orderData.orderId,
                shopType: 'ebay',
                shopId: shop.id,
                shopTitle: shop.title,
                shop: { shopType: 'ebay' },
                orderDate: orderData.creationDate,
                orderStatus: OrderStatus.PendingFullfillment,
                customerName: orderData.buyer?.username,
                customerAddressCountry: countryCodes.findOne('countryCode', orderData.fulfillmentStartInstructions[0].shippingStep.shipTo.contactAddress.countryCode)?.countryNameEn || '',
                customerAddressCountryCode: orderData.fulfillmentStartInstructions[0].shippingStep.shipTo.contactAddress.countryCode,
                createdAt: orderData.creationDate,
                updatedAt: orderData.lastModifiedDate,
                tasks: [],
                orderProductsVariations: orderData.lineItems.map((item: any) => ({
                  quantity: item.quantity,
                  adVariation: {
                    ad: {
                      title: item.title,
                    },
                    variationTypeValues: [],
                  },
                })),
              }));
            }
            case 'leboncoin': {
              const leboncoinOrders = await postMessageToLeboncoinExt(window, {
                action: 'get-orders',
              });
              return leboncoinOrders.orders.map((orderData: any) => ({
                id: '',
                orderId: String(orderData.id),
                shopType: 'leboncoin',
                shopId: shop.id,
                shopTitle: shop.title,
                shop: { shopType: 'leboncoin' },
                orderDate: orderData.created_at,
                orderStatus: OrderStatus.PendingFullfillment,
                customerName: orderData.buyer.pseudo,
                customerAddressCountry: 'France',
                customerAddressCountryCode: 'FR',
                createdAt: orderData.created_at,
                updatedAt: orderData.created_at,
                tasks: [],
                orderProductsVariations: [{
                  id: '',
                  quantity: 1,
                  adVariation: {
                    id: '',
                    ad: {
                      id: '',
                      title: orderData.item.name,
                    },
                    variationTypeValues: [],
                  },
                }],
              }));
            }
            case 'temu': {
              const response = await api.get(`/temu/orders?shop_id=${encodeURIComponent(shop.id)}`);
              const temuOrders = await response.json();
              return temuOrders.map((orderData: any) => ({
                id: '',
                orderId: orderData.parentOrderMap.parentOrderSn,
                shopType: 'temu',
                shopId: shop.id,
                shopTitle: shop.title,
                shop: { shopType: 'temu' },
                orderDate: orderData.parentOrderMap.parentOrderTime * 1000,
                orderStatus: OrderStatus.PendingFullfillment,
                customerName: '',
                customerAddressCountry: regionsAndSitesData_bySiteId[orderData.parentOrderMap.siteId].fullName,
                customerAddressCountryCode: regionsAndSitesData_bySiteId[orderData.parentOrderMap.siteId].abbreviation,
                createdAt: orderData.parentOrderMap.parentOrderTime * 1000,
                updatedAt: orderData.parentOrderMap.parentOrderTime * 1000,
                tasks: [],
                orderProductsVariations: orderData.orderList.map((subOrder: any) => {
                  return {
                    id: '',
                    quantity: subOrder.quantity,
                    adVariation: {
                      id: '',
                      ad: {
                        id: '',
                        title: subOrder.goodsName,
                      },
                      variationTypeValues: [],
                    },
                  }
                }),
              }));
            }
            case 'aliexpress': {
              const params = new URLSearchParams(aliexpressParams);
              params.set('shop_id', shop.id);
              const response = await api.get('/aliexpress/seller/orders?' + params.toString());
              const aliexpressOrders = await response.json();
              return (aliexpressOrders?.aliexpress_trade_seller_orderlist_get_response?.result?.target_list?.aeop_order_item_dto || []).map((orderData: any) => ({
                id: '',
                orderId: String(orderData.order_id),
                shopType: 'aliexpress',
                shopId: shop.id,
                shopTitle: shop.title,
                shop: { shopType: 'aliexpress' },
                orderDate: orderData.gmt_create,
                orderStatus: OrderStatus.PendingFullfillment,
                customerName: orderData.buyer_signer_fullname,
                customerAddressCountry: 'todo',
                customerAddressCountryCode: 'FR',
                createdAt: orderData.gmt_create,
                updatedAt: orderData.gmt_update,
                tasks: [],
                orderProductsVariations: orderData.product_list.aeop_order_product_dto.map((productData: any) => ({
                  id: '',
                  quantity: productData.product_count,
                  adVariation: {
                    id: '',
                    ad: {
                      id: '',
                      title: productData.product_name,
                    },
                    variationTypeValues: [],
                  },
                })),
              }));
            }
            default:
              return [];
          }
        })
      );

      const allOrders = shopOrderResults.flat();

      const { data: existingOrders } = await graphqlClient.query({
        query: CHECK_EXISTING_ORDERS_QUERY,
        variables: { extIdsAndShopTypes: allOrders.map(({ orderId, shopType, shopId }) => ({ id: orderId, shopType, shopId })) }
      });

      let allOrdersToImport = allOrders.filter(({ orderId, shopType, shopId }) => (
        !existingOrders.orders.nodes.some(
          (existingOrder: any) => (
            existingOrder.orderId == orderId &&
            existingOrder.shop.shopType === shopType &&
            existingOrder.shopId === shopId
          )
        )
      ));

      allOrdersToImport.sort((a, b) => dayjs(b.orderDate).diff(dayjs(a.orderDate)));

      setOrders(allOrdersToImport);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch orders');
      toast.error('Failed to load import orders');
    } finally {
      setLoading(false);
    }
  }, [importableShops, selectedShopIds, manualImportLang]);

  const toggleShopSelection = (shopId: string) => {
    setSelectedShopIds((prev) => {
      const next = new Set(prev);
      if (next.has(shopId)) {
        next.delete(shopId);
      } else {
        next.add(shopId);
      }
      return next;
    });
  };

  const [syncFromExternalOrder] = useMutation<
    ImportOrder_SyncFromExternalOrderMutation,
    ImportOrder_SyncFromExternalOrderMutationVariables
  >(
    gql`
      mutation ImportOrder_SyncFromExternalOrder($shopId: ID!, $shopType: ID!, $externalOrderId: String!, $externalOrderLang: String!, $externalOrderData: JSON) {
        syncFromExternalOrder(shopId: $shopId, shopType: $shopType, externalOrderId: $externalOrderId, externalOrderLang: $externalOrderLang, externalOrderData: $externalOrderData) {
          process {
            id
          }
          result
        }
      }
    `,
  );

  const importOrder = async ({
    shopId,
    shopType,
    externalOrderId,
    externalOrderLang,
  }: {
    shopId: string,
    shopType: SALE_CHANNEL,
    externalOrderId: string,
    externalOrderLang: string,
  }) => {
    let externalOrderData = null;
    if (shopType === 'leboncoin') {
      externalOrderData = await postMessageToLeboncoinExt(window, {
        action: 'get-single-order',
        orderId: externalOrderId,
      })

      if (externalOrderData.step.value === 'reserved') {
        await postMessageToLeboncoinExt(window, {
          action: 'confirm-availability',
          orderId: externalOrderId,
        })
        // Refresh the order data
        externalOrderData = await postMessageToLeboncoinExt(window, {
          action: 'get-single-order',
          orderId: externalOrderId,
        })
      }
    }

    trackRequest(
      syncFromExternalOrder({
        variables: {
          shopId: shopId,
          shopType: shopType as SALE_CHANNEL,
          externalOrderData: externalOrderData,
          externalOrderId: externalOrderId,
          externalOrderLang: externalOrderLang,
        },
      }),
      {
        title: `Importing order from ${shopType} : ${externalOrderId}`,
        isBlocking: true,
      }
    ).then(({ data }) => {
      navigate(`/orders/${data!.syncFromExternalOrder.result.id}`);
    });
  }


  const handleManualImport = async () => {
    if (!manualImportOrderId.trim()) {
      toast.error('Please enter an order ID');
      return;
    }

    const manualImportShop = importableShops.find((shop) => shop.id === manualImportShopId);

    if (!manualImportShop) {
      toast.error('Please select a shop');
      return;
    }

    setManualImportLoading(true);
    try {
      await importOrder({
        shopId: manualImportShop.id,
        shopType: manualImportShop.shopType,
        externalOrderId: manualImportOrderId.trim(),
        externalOrderLang: manualImportLang,
      });
      // Reset form
      setManualImportOrderId('');
      setManualImportLang('FR');
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to import order');
    } finally {
      setManualImportLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent"></div>
            <p className="mt-2 text-gray-600">Loading orders...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <div className="shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Orders</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={fetchOrders}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <MdRefresh className="-ml-0.5 mr-2 h-4 w-4" />
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Import Orders</title>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Shop Selection */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">Shops
            <Button size="sm" preset="primary" onClick={() => setSelectedShopIds(new Set(importableShops.map((shop) => shop.id)))}>
              Select All
            </Button>
          </h2>
          {importableShops.length === 0 ? (
            <p className="text-sm text-gray-500">No importable shops found. Add shops from the Shops page.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {importableShops.map((shop) => (
                <label
                  key={shop.id}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedShopIds.has(shop.id)}
                    onChange={() => toggleShopSelection(shop.id)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{shop.title}</div>
                    <div className="text-xs text-gray-500">{shop.shopType}</div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Manual Import Form
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Manual Import</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div>
              <label htmlFor="shop" className="block text-sm font-medium text-gray-700 mb-1">
                Shop
              </label>
              <select
                id="shop"
                value={manualImportShopId}
                onChange={(e) => {
                  const shop = importableShops.find((s) => s.id === e.target.value);
                  setManualImportShopId(e.target.value);
                  if (shop?.shopType === 'ebay' || shop?.shopType === 'leboncoin') {
                    setManualImportLang('FR');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                {importableShops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.title} ({shop.shopType})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="orderId" className="block text-sm font-medium text-gray-700 mb-1">
                Order ID
              </label>
              <input
                id="orderId"
                type="text"
                value={manualImportOrderId}
                onChange={(e) => setManualImportOrderId(e.target.value)}
                placeholder="Enter order ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            
            <div>
              <label htmlFor="lang" className="block text-sm font-medium text-gray-700 mb-1">
                Language
              </label>
              <input
                id="lang"
                type="text"
                value={manualImportLang}
                onChange={(e) => setManualImportLang(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500"
              />
            </div>

            <div className="md:col-span-2">
              <Button
                onClick={handleManualImport}
                disabled={manualImportLoading || !manualImportOrderId.trim() || !manualImportShopId}
                preset="primary"
                className="w-full"
              >
                {manualImportLoading ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <MdAdd size={20} className="mr-2" />
                    Import Order
                  </>
                )}
              </Button>
            </div>
          </div>
        </div> */}

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Available Orders ({orders.length})</h1>
          <div className="flex gap-4">
            {loading && (
              <div className="flex items-center text-gray-500">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-gray-500 border-t-transparent mr-2"></div>
                Refreshing...
              </div>
            )}
            
            <Button
              size="sm"
              onClick={fetchOrders}
              preset="white"
              disabled={loading || selectedShopIds.size === 0}
            >
              <MdRefresh size={20} />
              Refresh
            </Button>
          </div>
        </div>

        {selectedShopIds.size === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <h3 className="text-sm font-medium text-gray-900">Select shops to import orders</h3>
            <p className="mt-1 text-sm text-gray-500">Choose one or more shops above to see available orders.</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No orders to import</h3>
            <p className="mt-1 text-sm text-gray-500">All available orders have been imported.</p>
            <div className="mt-6">
              <button
                type="button"
                onClick={fetchOrders}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <MdRefresh className="-ml-1 mr-2 h-5 w-5" />
                Check for New Orders
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Header Row */}
            <div className="grid grid-cols-12 bg-gray-50 divide-x divide-gray-200">
              <div className="col-span-2 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order Info
              </div>
              <div className="col-span-2 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </div>
              <div className="col-span-7 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Items
              </div>
              <div className="col-span-1 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </div>
            </div>
            
            {/* Data Rows */}
            <div className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <OrderRow
                  key={`${order.shopId}-${order.orderId}`}
                  order={order}
                  handleRowClick={() => importOrder({
                    shopId: order.shopId,
                    shopType: order.shop?.shopType ?? order.shopType,
                    externalOrderId: order.orderId,
                    externalOrderLang: order.customerAddressCountryCode,
                  })}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <Outlet />
    </>
  );
}

export default withScreen(ImportOrderList); 